import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Chrome, Shield, User } from "lucide-react";
import { NSBLogo } from "../components/NSBLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const mode = searchParams.get("mode"); // "admin" | "player" | null (coach default)
  const from = location.state?.from || "/dashboard";

  const pageConfig = useMemo(() => {
    if (mode === "admin") return {
      heading: "Administrative Login",
      subtext: "Sign in to access the NSB Admin Dashboard",
      icon: <Shield className="w-5 h-5 text-red-500" />,
      brandHeading: "NSB Administration",
      brandSubtext: "Manage athletes, evaluations, blog posts, and platform settings.",
    };
    if (mode === "player") return {
      heading: "Player Login",
      subtext: "Sign in to access your Athlete Dashboard",
      icon: <User className="w-5 h-5 text-blue-500" />,
      brandHeading: "Athlete Portal",
      brandSubtext: "View your evaluations, performance metrics, and recruiting updates.",
    };
    return {
      heading: "Welcome Back",
      subtext: "Sign in to access your dashboard",
      icon: null,
      brandHeading: "Turning Talent Into\nOpportunity",
      brandSubtext: "Access your athlete dashboard, view evaluations, and connect with coaches.",
    };
  }, [mode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });

      let data;
      try {
        // Use .text() + manual JSON.parse to bypass platform fetch interceptor
        // that can consume the response body ("Object cannot be cloned" error)
        const text = await response.text();
        data = JSON.parse(text);
      } catch {
        // Platform interceptor may have consumed the body entirely.
        // Check if login actually succeeded via session cookie
        try {
          const meRes = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
          if (meRes.ok) {
            const meText = await meRes.text();
            const meData = JSON.parse(meText);
            toast.success("Welcome back!");
            const roleRoutes = { athlete: "/dashboard/athlete", coach: "/dashboard/coach", scout: "/dashboard/scout", agent: "/dashboard/agent", admin: "/dashboard/admin" };
            navigate(roleRoutes[meData.role] || "/dashboard/athlete", { replace: true });
            return;
          }
        } catch {}
        throw new Error("Invalid credentials");
      }

      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      toast.success("Welcome back!");
      
      const roleRoutes = {
        athlete: "/dashboard/athlete",
        coach: "/dashboard/coach",
        scout: "/dashboard/scout",
        agent: "/dashboard/agent",
        admin: "/dashboard/admin",
      };
      
      navigate(roleRoutes[data.user.role] || "/dashboard/athlete", { replace: true });
    } catch (error) {
      toast.error(error.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url(https://gopsusports.com/imgproxy/R-N0KhC_W-z5R6-zXRmmuRYzKcB9KwIt2fRzCgm7GEA/rs:fit:1980:0:0/g:ce/q:90/aHR0cHM6Ly9zdG9yYWdlLmdvb2dsZWFwaXMuY29tL2dvcHN1c3BvcnRzLXByb2QvMjAyMy8wNy8xNS9ENnJaYmJWRXNnSDRxVVQ5em9XTENGZUNOWU45bjZacTBCN3RIc2VXLmpwZw.jpg)`,
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center">
            <NSBLogo className="h-14" light={true} />
          </Link>
          
          <div className="max-w-md">
            <h1 className="text-4xl font-black leading-tight mb-4">
              {pageConfig.brandHeading.split('\n').map((line, i) => (
                <span key={i}>{i > 0 && <br />}{i > 0 ? <span className="text-red-400">{line}</span> : line}</span>
              ))}
            </h1>
            <p className="text-white/80">
              {pageConfig.brandSubtext}
            </p>
          </div>

          <div className="flex items-center gap-8 text-sm text-white/60">
            <span>450+ Partner Colleges</span>
            <span>6,000+ Coaches</span>
            <span>98% Success Rate</span>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center">
              <NSBLogo className="h-12" light={false} />
            </Link>
          </div>

          <div className="text-center lg:text-left mb-8">
            <div className="flex items-center gap-2 justify-center lg:justify-start">
              {pageConfig.icon}
              <h2 className="text-3xl font-bold text-nsb-navy" data-testid="login-heading">{pageConfig.heading}</h2>
            </div>
            <p className="text-gray-500 mt-2">{pageConfig.subtext}</p>
          </div>

          {/* Google Login */}
          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white border-2 border-gray-200 text-blue-900 hover:bg-gray-50 flex items-center justify-center gap-3 py-6 mb-6"
            data-testid="google-login-btn"
          >
            <Chrome size={20} className="text-blue-900" />
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-4 text-gray-500 uppercase">Or continue with email</span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-gray-600">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900 focus:ring-blue-900"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-gray-600">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900 focus:ring-blue-900"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary py-6 flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <ArrowRight size={18} />}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-blue-900 font-semibold hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
